import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Organization, PaginatedOrganizationDTO } from '../models/organization';
import { User, PaginatedUserDTO, UserInfo } from '../models/user-model';
import { Report, PaginatedReportDTO } from '../models/report';
import { environment } from '../../environments/environment';
import { URLSearchParams } from 'url';
import { PaginatedRequest } from '../models/paginated-request';
import { AtcApprover } from '../models/atc-approver';


@Injectable()
export class UserService {

  baseUrl = environment.userServiceBaseUrl;

  constructor(private http: HttpClient) {
  }

  getCurrentUser(userId: string){
    return new Promise<void>(resolve => {
      this.findUserById(userId).subscribe(user => {
        localStorage.setItem('currentUserMarketIds', JSON.stringify((user && user.isAdmin && user.markets) ? user.markets : []));
        resolve();
      });
   });
  }

  isMarketPermittedForCurrentUser(marketId: string): boolean {
    let currentMarketIds = localStorage.getItem('currentUserMarketIds');
    let mIds: string[] = JSON.parse(currentMarketIds);
    if (!mIds || (mIds.length && !mIds.includes(marketId))) {
      return false;
    } else {
      return true;
    }
  }

  async updateCurrentUserMarketIds(userId: string) {
      await this.getCurrentUser(userId);
  }

  getOrganizationsForListing(paginatedRequest: PaginatedRequest, orgSearchText: string) {
    const url = `${this.baseUrl}organizations/listing`;
    const headers = new HttpHeaders().set('Accept', 'application/json');
    const params = new HttpParams()
      .set('page', paginatedRequest.page)
      .set('size', paginatedRequest.pageSize)
      .set('sort', paginatedRequest.sorting)
      .set('searchText', orgSearchText);
    return this.http.get<PaginatedOrganizationDTO>(url, { params, headers });
  }

  getOrganizations() {
    const url = `${this.baseUrl}organizations`;
    const headers = new HttpHeaders()
      .set('Accept', 'application/json');
    const params = new HttpParams()
      .set('page', '0')
      .set('size', '1000')
      .set('sort', 'name,asc');
    return this.http.get<PaginatedOrganizationDTO>(url, { params, headers })
      .map(response => response.records);
  }

  saveOrganization(organization: Organization) {
    const headers = new HttpHeaders()
      .set('Accept', 'application/json');
    if (organization['Organization ID']) {
      const url = `${this.baseUrl}organizations/${organization['Organization ID']}`;
      return this.http.put<Organization>(url, organization, { headers });
    } else {
      const url = `${this.baseUrl}organizations`;
      return this.http.post<Organization>(url, organization, { headers });
    }
  }

  findOrganizationById(id: string) {
    const url = `${this.baseUrl}organizations/${id}`;
    const headers = new HttpHeaders()
      .set('Accept', 'application/json');
    return this.http.get<Organization>(url, { headers });
  }

  findUserById(uuid: string) {
    const url = `${this.baseUrl}users/${uuid}`;
    const headers = new HttpHeaders()
      .set('Accept', 'application/json');
    return this.http.get<User>(url, { headers });
  }

  findUserByOrganizationIdAndText(id: string, text: string) {
    const url = `${this.baseUrl}users/byOrgIdAndText`;
    const headers = new HttpHeaders()
      .set('Accept', 'application/json');
    const params = new HttpParams()
      .set('organizationID', id)
      .set('text', text)
      .set('page', '0')
      .set('size', '100');
    return this.http.get<PaginatedUserDTO>(url, { params, headers })
      .map(response => response.records);
  }

  getUsersWithPermission(userList: any){
    const url = `${this.baseUrl}users/byUserIds`;
    const headers = new HttpHeaders().set('Accept', 'application/json');
    const params = new HttpParams().set('userListString', userList);
    return this.http.get<User[]>(url, { params, headers });
  }

  getUsers() {
    const url = `${this.baseUrl}users`;
    const headers = new HttpHeaders()
      .set('Accept', 'application/json');
    const params = new HttpParams()
      .set('page', '0')
      .set('size', '2000');
    return this.http.get<PaginatedUserDTO>(url, { params, headers })
      .map(response => response.records);
  }

  saveUser(user: User) {
    const headers = new HttpHeaders()
      .set('Accept', 'application/json');
    if (user['User ID']) {
      const url = `${this.baseUrl}users/${user['User ID']}`;
      return this.http.put<User>(url, user, { headers });
    } else {
      const url = `${this.baseUrl}users`;
      return this.http.post<User>(url, user, { headers });
    }
  }

  removeUser(uuid: string) {
    const url = `${this.baseUrl}users/${uuid}`;
    const headers = new HttpHeaders()
      .set('Accept', 'application/json');
    return this.http.delete<User>(url, { headers });
  }

  removeOrganization(id: string) {
    const url = `${this.baseUrl}organizations/${id}`;
    const headers = new HttpHeaders()
      .set('Accept', 'application/json');
    return this.http.delete<Organization>(url, { headers });
  }

  getReports(organizationID: string) {
    const url = `${this.baseUrl}reports`;
    const headers = new HttpHeaders()
      .set('Accept', 'application/json');
    const params = new HttpParams()
      .set('organizationID', organizationID)
      .set('page', '0')
      .set('size', '500');
    return this.http.get<PaginatedReportDTO>(url, { params, headers })
      .map(response => response.records);
  }

  getAllReports() {
    const url = `${this.baseUrl}reports`;
    const headers = new HttpHeaders()
      .set('Accept', 'application/json');
    const params = new HttpParams()
      .set('page', '0')
      .set('size', '500');
    return this.http.get<PaginatedReportDTO>(url, { params, headers })
      .map(response => response.records);
  }

  createReport(report: Report) {
    const headers = new HttpHeaders()
      .set('Accept', 'application/json');
    const url = `${this.baseUrl}reports`;
    return this.http.post<Report>(url, report, { headers });
  }

  deleteReport(reportId: string) {
    const url = `${this.baseUrl}reports/${reportId}`;
    const headers = new HttpHeaders()
      .set('Accept', 'application/json');
    return this.http.delete<void>(url, { headers });
  }

  getUsersFilteringByOrgId(organizationId: string) {
    const url = `${this.baseUrl}usersFilter`;
    const headers = new HttpHeaders()
      .set('Accept', 'application/json');
    const params = new HttpParams()
      .set('organizationID', organizationId)
      .set('page', '0')
      .set('size', '2000');
    return this.http.get<PaginatedUserDTO>(url, { params, headers })
      .map(response => response.records);
  }

  getUserDisplayNames(userIds: string[]) {
    const headers = new HttpHeaders()
      .set('Accept', 'application/json');
    const url = `${this.baseUrl}userDisplayNames`;
    const params = new HttpParams()
      .set('page', '0')
      .set('size', '2000');
    return this.http.post<UserInfo[]>(url, userIds, { params, headers });
  }

  getAllAtcApprovers(marketId: string) {
    const headers = new HttpHeaders()
      .set('Accept', 'application/json');
    const params = new HttpParams()
      .set('marketId', marketId);
    const url = `${this.baseUrl}approvers/atc`;
    return this.http.get<AtcApprover[]>(url, {params, headers});
  }

  getUsersByOrgId(orgId: string) {
    const headers = new HttpHeaders()
      .set('Accept', 'application/json');
    const url = `${this.baseUrl}users/byOrgId/${orgId}`;
    return this.http.get<User[]>(url, {headers});
  }

  getUsersByOrgIdAndMarketId(orgId: string, marketId: string) {
    const headers = new HttpHeaders()
      .set('Accept', 'application/json');
    const url = `${this.baseUrl}users/byOrgId/${orgId}/${marketId}`;
    return this.http.get<User[]>(url, {headers});
  }

  getVerizonApproversByOrgIdMarketId(orgId: string, marketId: string) {
    const headers = new HttpHeaders()
      .set('Accept', 'application/json');
    const url = `${this.baseUrl}approvers/verizon/byOrgId/${orgId}/${marketId}`;
    return this.http.get<User[]>(url, {headers});
  }

  getOasisUsersGuide() {
    const url = `${this.baseUrl}reports/usersGuide`;
    const headers = new HttpHeaders()
      .set('Accept', 'application/json');
    return this.http.get<User>(url, { headers });
  }

  getMarketPermittedForCurrentUser(): string[] {
    let currentMarketIds = localStorage.getItem('currentUserMarketIds');
    let mIds: string[] = JSON.parse(currentMarketIds);
    if (mIds && mIds.length > 0) {
      return mIds;
    } else {
      return [];
    }
  }

  getOrganizationsForUser() {
    const url = `${this.baseUrl}organizations/list`;
    const headers = new HttpHeaders()
      .set('Accept', 'application/json');
    return this.http.get<Organization[]>(url, { headers });
  }
}





// WEBPACK FOOTER //
// /codebuild/output/src3408204168/src/bitbucket.org/tesscotds/webclient/src/api/services/user.service.ts